import React from 'react';
import { motion } from 'framer-motion';
import { HERO } from '../data/content';
import { ArrowDown, Sparkles } from 'lucide-react';
import DataFlowNetwork from './DataFlowNetwork';

export default function Hero() {
  return (
    <section id="top" className="relative pt-32 pb-24 overflow-hidden bg-gradient-to-b from-[#F5F6FA] via-white to-white">
      {/* Subtle radial accents */}
      <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-[color:var(--red-soft)] blur-3xl opacity-70" />
      <div className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full bg-[#E8ECFB] blur-3xl opacity-60" />

      <div className="container-x relative z-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 border border-line rounded-full px-4 py-1.5 bg-white/60 backdrop-blur"
          >
            <Sparkles size={14} className="text-red" />
            <span className="text-[11px] tracking-[0.22em] uppercase font-semibold text-navy">{HERO.eyebrow}</span>
          </motion.div>

          <div className="mt-6 font-display leading-[0.95]">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.05 }}
              className="text-[54px] md:text-[76px] xl:text-[92px] text-[color:var(--ink)]"
            >
              {HERO.title1}
            </motion.h1>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="text-[54px] md:text-[76px] xl:text-[92px] text-stroke"
            >
              {HERO.title2}
            </motion.h1>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.25 }}
              className="text-[54px] md:text-[76px] xl:text-[92px]"
            >
              <span className="grad-text-red">Professional </span>
              <span className="text-[color:var(--ink)]">Program</span>
            </motion.h1>
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.35 }}
            className="mt-8 text-[17px] md:text-[18px] text-[color:var(--muted)] max-w-xl leading-relaxed"
          >
            {HERO.description}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.45 }}
            className="mt-10 flex flex-wrap gap-3"
          >
            {HERO.stats.map((s, i) => (
              <div
                key={i}
                className="bg-white border border-line rounded-2xl px-5 py-3 min-w-[150px] shadow-[0_1px_0_0_rgba(41,56,145,0.03)]"
              >
                <div className="font-display text-[22px] text-[color:var(--ink)] leading-tight">{s.value}</div>
                <div className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[color:var(--muted)] mt-1">
                  {s.label}
                </div>
              </div>
            ))}
          </motion.div>

          <motion.a
            href="#overview"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.55 }}
            className="mt-12 inline-flex items-center gap-3 text-sm text-[color:var(--ink)]"
          >
            <span className="w-9 h-9 rounded-full border border-line flex items-center justify-center">
              <ArrowDown size={16} />
            </span>
            Scroll to explore the program
          </motion.a>
        </div>

        <div className="lg:col-span-5 w-full" data-testid="hero-visual">
          <DataFlowNetwork />
        </div>
      </div>
    </section>
  );
}
